import type { AxiosError } from 'axios'
import { CalendarClock, Plus, UserRoundX, X } from 'lucide-react'
import { useMemo, useState } from 'react'
import { useNavigate } from 'react-router'
import { toast } from 'react-toastify'
import {
	useCreateCommitmentException,
	useDeleteCommitmentException,
	useMyCommitmentExceptions,
} from '@/api/queries/commitment-matrix-queries'
import { usePeople } from '@/api/queries/people-queries'
import {
	CommitmentExceptionFormContent,
	type CommitmentExceptionFormValues,
} from '@/components/administration/commitment-matrix/commitment-matrix-exception-form'
import {
	AdminCardPage,
	ConfirmDialog,
	ResponsiveFormModal,
} from '@/components/commons'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { ROUTES } from '@/constants/routes'
import { useAuthStore } from '@/store'
import type { CommitmentException, CommitmentExceptionReason } from '@/types'
import { getApiErrorMessage } from '@/utils/api-error'

const REASON_LABELS: Record<CommitmentExceptionReason, string> = {
	FERIAS: 'Férias',
	ATESTADO: 'Atestado',
	AFASTAMENTO: 'Afastamento',
	TRANSFERIDO: 'Transferido',
	FOLGA_DE_CAMPO: 'Folga de campo',
	EM_MOBILIZACAO: 'Em mobilização',
	LICENCA_MATERNIDADE: 'Licença maternidade',
	OUTRO: 'Outro',
}

const STATUS_LABELS: Record<CommitmentException['status'], string> = {
	PENDENTE: 'Pendente',
	APROVADO: 'Aprovado',
	REJEITADO: 'Rejeitado',
}

const STATUS_BADGE_CLASSES: Record<CommitmentException['status'], string> = {
	PENDENTE: 'bg-kaefer-honey-yellow/20 text-kaefer-dark-grey',
	APROVADO: 'bg-kaefer-grass-green/20 text-kaefer-dark-grey',
	REJEITADO: 'bg-kaefer-red/20 text-kaefer-dark-grey',
}

export function MyCommitmentExceptionsPage() {
	const navigate = useNavigate()
	const user = useAuthStore((s) => s.user)

	const { data: people } = usePeople(true)
	const myPersonId = useMemo(
		() => people?.find((p) => p.badge === user?.badge)?.id ?? null,
		[people, user?.badge],
	)

	const { data: exceptionsData, isLoading } = useMyCommitmentExceptions({
		limit: 20,
	})
	const exceptions = exceptionsData?.items ?? []

	const [formOpen, setFormOpen] = useState(false)
	const [cancelingException, setCancelingException] =
		useState<CommitmentException | null>(null)
	const createMutation = useCreateCommitmentException()
	const deleteMutation = useDeleteCommitmentException()

	const handleSave = (values: CommitmentExceptionFormValues) => {
		createMutation.mutate(values, {
			onSuccess: () => {
				toast.success('Pedido de exceção enviado para aprovação.')
				setFormOpen(false)
			},
			onError: (error) => toast.error(getApiErrorMessage(error)),
		})
	}

	const handleConfirmCancel = () => {
		if (!cancelingException) return

		deleteMutation.mutate(
			{ id: cancelingException.id },
			{
				onSuccess: () => {
					toast.success('Pedido cancelado.')
					setCancelingException(null)
				},
				onError: (error) => {
					const message = (error as AxiosError<{ message?: string }>).response
						?.data?.message
					toast.error(
						message ??
							'Este pedido já foi decidido e não pode mais ser cancelado.',
					)
					setCancelingException(null)
				},
			},
		)
	}

	return (
		<AdminCardPage
			icon={<UserRoundX className="size-5" />}
			title="Minhas Exceções"
			subtitle="Solicite férias, atestado ou afastamento para sair do cálculo de comprometimento no período."
			onBack={() => navigate(ROUTES.DASHBOARD.HOME)}
			modal={
				<ResponsiveFormModal
					open={formOpen}
					onOpenChange={setFormOpen}
					title="Solicitar exceção"
					dialogClassName="max-h-[85vh] overflow-y-auto sm:max-w-md"
				>
					<CommitmentExceptionFormContent
						lockedPersonId={myPersonId ?? undefined}
						onSave={handleSave}
						onClose={() => setFormOpen(false)}
						isSaving={createMutation.isPending}
					/>
				</ResponsiveFormModal>
			}
		>
			<div className="flex justify-end">
				<Button
					onClick={() => setFormOpen(true)}
					className="gap-2 bg-kaefer-medium-grey text-white hover:bg-kaefer-medium-grey/90 w-full sm:w-auto"
				>
					<Plus className="size-4" />
					<span>Solicitar exceção</span>
				</Button>
			</div>

			<div className="flex flex-col gap-3">
				{isLoading ? (
					<p className="py-10 text-center text-sm text-muted-foreground">
						Carregando...
					</p>
				) : exceptions.length === 0 ? (
					<p className="py-10 text-center text-sm text-muted-foreground">
						Nenhum pedido de exceção encontrado.
					</p>
				) : (
					exceptions.map((exception) => {
						const requestedByOther =
							exception.requestedBy !== exception.personId

						return (
							<div
								key={exception.id}
								className="rounded-lg border border-kaefer-light-silver bg-white p-4"
							>
								<div className="flex flex-wrap items-start justify-between gap-3">
									<div className="min-w-0 flex flex-col gap-1">
										<div className="flex items-center gap-1.5 text-sm text-kaefer-medium-grey">
											<CalendarClock className="size-3.5" />
											<span>
												{formatDate(exception.startDate)} —{' '}
												{formatDate(exception.endDate)}
											</span>
										</div>
										<div className="text-xs text-muted-foreground">
											Para: {exception.personName}
										</div>
										{requestedByOther && (
											<div className="text-xs text-muted-foreground">
												Solicitado por: {exception.requestedByName}
											</div>
										)}
										{exception.status !== 'PENDENTE' &&
											exception.reviewedByName && (
												<div className="text-xs text-muted-foreground">
													{exception.status === 'APROVADO'
														? 'Aprovado'
														: 'Rejeitado'}{' '}
													por {exception.reviewedByName}
													{exception.reviewedAt &&
														` em ${formatDateTime(exception.reviewedAt)}`}
												</div>
											)}
									</div>

									<div className="flex items-center gap-2">
										<Badge className="bg-kaefer-light-silver/70 text-kaefer-dark-grey">
											{REASON_LABELS[exception.reason]}
										</Badge>
										<Badge className={STATUS_BADGE_CLASSES[exception.status]}>
											{STATUS_LABELS[exception.status]}
										</Badge>
										{exception.status === 'PENDENTE' && (
											<Button
												type="button"
												variant="ghost"
												size="icon-circle"
												onClick={() => setCancelingException(exception)}
												disabled={deleteMutation.isPending}
												className="text-kaefer-red hover:bg-transparent hover:text-kaefer-red/70"
												title="Cancelar pedido"
											>
												<X className="size-4" />
											</Button>
										)}
									</div>
								</div>
							</div>
						)
					})
				)}
			</div>

			<ConfirmDialog
				open={!!cancelingException}
				onOpenChange={(open) => {
					if (!open) setCancelingException(null)
				}}
				title="Cancelar pedido"
				onConfirm={handleConfirmCancel}
				isPending={deleteMutation.isPending}
			>
				Tem certeza que deseja cancelar este pedido de exceção?
			</ConfirmDialog>
		</AdminCardPage>
	)
}

function formatDate(iso: string): string {
	const [year, month, day] = iso.split('-')
	return `${day}/${month}/${year}`
}

function formatDateTime(iso: string): string {
	return new Date(iso).toLocaleString('pt-BR', {
		day: '2-digit',
		month: '2-digit',
		year: 'numeric',
		hour: '2-digit',
		minute: '2-digit',
	})
}
